import { DEFAULT_PALETTE_ID, paletteById } from "./theme";

/**
 * 화면 문구 — 운영자가 배포 없이 고치는 텍스트(FR-LAND).
 *
 * 홈 화면에서 시작했지만 지금은 **전자책 고르기·문의 안내**까지 다룬다.
 * 넣는 기준: 행사·정책·톤에 따라 **바뀔 만한 말**. 조작 설명(환영 팝업 4단계 같은)은
 * 안 넣는다 — 그건 기능이 바뀌면 같이 바뀌어야 하는 것이라 코드와 붙어 있어야 한다.
 *
 * 왜 콘텐츠인가: "체험판 공개 → 정식 오픈", "여름 축제 중" 같은 문구는 행사 주기마다 바뀐다.
 * 코드에 박혀 있으면 한 줄 고치는 데 배포가 필요하다. 관리자가 직접 바꾸게 한다.
 *
 * 안전: **평문만** 저장한다. HTML·링크를 넣을 수 없게 태그 문자를 걸러내고 길이를 자른다
 * (React 가 이스케이프하지만, 저장 단계에서 막아야 다른 렌더 경로가 생겨도 안전하다).
 * 값이 비면 기본값을 쓴다 — 실수로 다 지워도 홈이 빈 화면이 되지 않는다.
 */
export interface SiteText {
  milestone: string; // 무대 위 작은 배지("체험판 공개"). 비우면 배지 자체가 안 보인다
  title1: string; // 큰 제목 첫 줄
  title2: string; // 큰 제목 둘째 줄(굵게)
  /**
   * 👶 **몇 살 것인지 — 첫 화면 제목 아래·책 고르기 화면에 작은 딱지로** (2026-10-02, 조치계획서 ■9·■1).
   *
   * 「첫 화면과 책 소개에 연령을 명시」를 약속했는데 **적을 칸이 없었다** — 보호자 안내(/guide)에만 있었다.
   * `|` 로 나눠 적으면 딱지 하나씩(4개까지). **영유아 모드에서도 보인다** — 보호자가 「이게 몇 살 것인지」를
   * 처음 보는 곳이 첫 화면이다(작은 배지·아래 안내는 영유아 모드에서 숨는다).
   *
   * 기본은 **비어 있다**: 운영자가 관리자에서 넣고 전후 화면을 남기는 것이 증빙이다(10. 31.).
   * 비우면 딱지가 사라진다. 나누는 판정은 `ageBandsOf` 한 곳.
   */
  ageBands: string;
  bubble: string; // 그레그 말풍선
  cta: string; // 큰 입장 버튼
  note: string; // 비회원 안내(히어로 없을 때)
  /**
   * 🧒 **처음 온 아이에게 「나만의 친구 만들기」.**
   *
   * 예전엔 첫 화면에 이 길이 **아예 없었다.** [체험 공간 입장]을 눌러 다음 화면까지 가야
   * 「히어로 만들기」가 나오고, 거기서도 큰 단추는 [들어가기](기본 히어로)였다.
   * 그래서 여권 코드가 91개인데 **제 친구를 끝까지 만든 아이는 1명**이었다(2026-08-31 집계).
   * 「나만의 히어로」는 이 서비스가 아이에게 주는 첫 번째 자기 것인데, 그 길이 숨어 있었다.
   *
   * 🔴 **비우면 그 단추만 사라진다** — 첫 화면을 단순하게 두고 싶으면 운영자가 지우면 된다.
   * 이미 제 친구가 있는 아이에게는 안 뜬다(그 자리는 「다시 왔군요」다).
   */
  makeHeroCta: string;
  newsEmpty: string; // 소식 버튼을 눌렀는데 소식이 없을 때

  // ── 전자책 고르기(여러 권일 때) — FR-BOOK-10 ──
  bookKicker: string; // 작은 윗말
  bookTitle: string; // 큰 제목
  bookSub: string; // 아래 안내(권수는 화면이 뒤에 붙인다)

  /**
   * 「이어서 읽기」 버튼 — 읽다 만 책이 있을 때만 첫 화면에 뜬다.
   * 뒤에 책 이름이 자동으로 붙으므로 여기에는 짧은 앞말만 적는다.
   */
  resumeCta: string;

  /* ── 체험 공간 들어가기(/v) ──────────────────────────────
   * 여기 문구가 예전엔 **「메타버스 입장」·「월드로 입장」** 이었다. 개발자 말이 화면에 남은 것이다.
   * 같은 곳을 랜딩에선 「체험 공간」, 하단 메뉴에선 「체험존」이라 부르는데 여기만 딴 이름이었다.
   * 아이도 보호자도 「메타버스」·「월드」를 모른다 — 그래서 문구를 운영자 손에 옮긴다. */
  verseTitle: string; // 화면 제목
  verseEnter: string; // 들어가는 큰 버튼
  verseNoteNew: string; // 히어로가 아직 없을 때 안내
  verseNoteSaved: string; // 저장된 히어로가 있을 때 안내

  /* ── 🧑‍🍼 보호자 안내(/guide) ──────────────────────────────
   * 보호자용 설명이 **설정 시트 안 몇 줄**이 전부였다. 아동 서비스에서 보호자가
   * "이게 뭐고, 안전한가, 개인정보는 어떻게 되나"를 못 찾으면 재방문이 안 일어난다.
   *
   * 칸은 넷으로 못 박는다(자유 목록이 아니다) — 운영자가 빈 페이지를 만들 수가 없고,
   * 안 쓸 칸은 **내용을 비우면 그 칸만 사라진다.** */
  guideTitle: string;
  guideIntro: string;
  guideSafetyTitle: string;
  guideSafetyBody: string;
  guidePrivacyTitle: string;
  guidePrivacyBody: string;
  guideClassTitle: string;
  guideClassBody: string;
  familyBookTitle: string;
  familyBookNoteTitle: string;
  familyBookNoteBody: string;
  familyBookLinkLabel: string;
  familyBookLinkUrl: string;
  guideTogetherTitle: string;
  guideTogetherBody: string;
  /**
   * 👶 **몇 살에 무엇을 하나 · 얼마나 보나** (2026-09-21 신설 — 조치계획서 ■1, 의견 ⑮·④·⑳).
   *
   * 중간평가에서 「부모의 선택과 지도가 필요한 만큼 관련 가이드가 추가 필요」를 받았다.
   * 안내 화면에 **연령과 이용 시간**을 말하는 칸이 아예 없었다 — 보호자가 「몇 살 것인지」를
   * 못 찾으면 사 주지도, 어린이집에 권하지도 않는다.
   */
  guideAgeTitle: string;
  guideAgeBody: string;
  /** 📷 카메라를 쓸 때 보호자가 알아야 할 것(밝은 곳·동반·사진은 기기 안에서만). */
  guideCameraTitle: string;
  guideCameraBody: string;

  // ── 문의 안내(마을 소식 화면 아래) — 아동안전 정책 설명 ──
  askTitle: string;
  askBody: string;
  askCta: string; // 외부 폼으로 가는 버튼

  /* ── 🎉 책을 다 읽었을 때 ────────────────────────────────
   * 34쪽을 다 넘겨도 **아무 일도 일어나지 않았다**(2026-08-23 발견). 마지막 쪽에 그대로 서고,
   * 다시 읽으려면 ‹ 를 서른네 번 눌러야 했다 — 이 나이 아이는 같은 책을 몇 번이고 다시 읽는데.
   *
   * 굿즈 줄이 있긴 했지만 **책에 굿즈를 붙였을 때만** 떴다. 안 붙인 책은 빈손이었다. */
  endTitle: string; // 큰 축하 한 줄
  endSub: string; // 아래 한 줄(비우면 안 보인다)
  endAgain: string; // 처음부터 다시 읽기 버튼
  endMore: string; // 다른 책 보러 가기 버튼
  endWorld: string; // 체험 공간으로 잇는 버튼(비우면 그 버튼만 사라진다)
  /** 👨‍👩‍👧 이번에 함께 본 마법의 보호자 문구를 접어 두는 줄 제목(비우면 그 줄이 사라진다). 기록은 이 기기·이 창에만 */
  endTogether: string;

  /**
   * 🎨 **AI 에게 알려 줄 그림체.** [➕ 직접 만들기]의 [AI로 그려보기]가 쓴다.
   *
   * 아이 화면에 나가는 글이 아닌데 여기 두는 이유: **운영자가 배포 없이 고치는 값**이라는
   * 점이 같고(팔레트 id 와 같은 자리), 값 하나 때문에 문서·라우트를 새로 만들 비용이 크다.
   * 책이 늘면 결이 달라진다 — 그때 여기서 고친다. 비우면 코드의 기본 결을 쓴다.
   */
  artStyle: string;

  /* ── 📖 내 그림책 — 문장 조각·제목 예시 (2026-10-01) ─────────────
   * 4~7세는 아직 문장을 못 쓴다. 「누가 + 어디서 + 무엇을」 조각을 눌러 한 쪽을 끝낸다.
   * 낱말은 `|` 로 나눠 적는다(한 무리 10개·낱말 16자까지). 책·행사가 바뀌면 운영자가 고친다.
   * 제목 예시의 `{이름}` 자리에는 아이 별명이 들어간다. */
  storyWho: string;
  storyWhere: string;
  storyWhat: string;
  storyTitles: string;
  /** 🧸 붙일 수 있는 스티커 — 그림 이름(`npc.<이름>`·`decor.<이름>`)을 `|` 로. 없는 그림은 화면이 뺀다 */
  storyStickers: string;

  /**
   * 색 팔레트 id. 문구가 아니지만 여기 둔다 — 값 하나 때문에 문서·라우트·마이그레이션을
   * 새로 만드는 비용이 크고, 이 문서가 이미 "운영자가 배포 없이 고치는 것"이다.
   * 모르는 값이면 기본 팔레트로 되돌아간다(아래 sanitize).
   */
  theme: string;
}

export const DEFAULT_SITE_TEXT: SiteText = {
  milestone: "체험판 공개",
  title1: "그레그와 병아리의",
  title2: "경주 그림책방",
  ageBands: "",
  bubble: "책 속으로 함께 떠나볼까?",
  cta: "체험 공간 입장 ▶️",
  note: "가입·로그인 없이 바로 시작 · 개인정보를 서버로 보내지 않아요",
  makeHeroCta: "🧒 나만의 친구 만들기",
  newsEmpty: "아직 새 소식이 없어요. 곧 알려드릴게요! 🐣",
  bookKicker: "그림책방",
  bookTitle: "어떤 이야기를 펼쳐 볼까요?",
  bookSub: "표지를 누르면 바로 읽을 수 있어요",
  resumeCta: "이어서 읽기",
  verseTitle: "체험 공간 들어가기",
  verseEnter: "들어가기 →",
  verseNoteNew:
    "아직 히어로가 없어요. 기본 히어로로 들어가거나, 먼저 나만의 히어로를 만들 수 있어요.",
  verseNoteSaved: "만들어 둔 히어로로 들어가요.",
  guideTitle: "보호자님께",
  guideIntro:
    "《그레그와 병아리의 경주 그림책방》은 그림책을 읽고, 체험 공간을 거닐고, 굿즈를 구경하는 아이·보호자용 놀이터예요. 가입도 로그인도 없어요.",
  guideSafetyTitle: "🧸 아이가 안전하게",
  guideSafetyBody:
    "댓글·글쓰기가 없어요. 아이가 낯선 사람의 글을 볼 일도, 자기 이야기를 남길 일도 없어요. 소리는 저절로 나지 않고, 눈이 아플 만큼 번쩍이는 연출도 넣지 않았어요. 바깥 사이트로 갈 때는 먼저 알려 드려요.",
  guidePrivacyTitle: "🔒 개인정보를 받지 않아요",
  guidePrivacyBody:
    "이름·이메일·전화번호를 묻지 않아요. 아이가 만든 히어로와 모은 별은 이 기기 안에만 있어요 — 저희 서버에는 없어요(방과후 수업은 아래를 봐 주세요). 굿즈 배송처럼 꼭 필요한 정보는 바깥 스토어에서만 입력해요. 설정에서 [처음부터 다시 하기]로 전부 지울 수 있어요.",
  guideClassTitle: "🏫 방과후 수업에 참여한다면",
  guideClassBody:
    "수업에서 만든 그림책은 반 친구들과 선생님이 볼 수 있게 저희가 보관해요. 그때도 이름·연락처는 받지 않아요 — 아이가 고른 별명과 여섯 글자 코드뿐이에요.",
  familyBookTitle: "📖 우리 아이 그림책",
  familyBookNoteTitle: "👪 집에서도 이어서",
  familyBookNoteBody:
    "아이가 수업에서 만든 그림책이에요. 다음 학기 수업과 그림책 굿즈 소식은 아래에서 볼 수 있어요.",
  familyBookLinkLabel: "다음 학기 알아보기",
  familyBookLinkUrl: "",
  guideTogetherTitle: "👪 이렇게 같이 놀아요",
  guideTogetherBody:
    "그림책은 소리를 켜고 함께 들어 보세요. 체험 공간에서는 아이가 걸어 다니며 친구들을 만나요. 경주에 가신다면 카드에 카메라를 비춰 보세요 — 안 될 때는 영상으로도 볼 수 있어요.",
  guideAgeTitle: "👶 몇 살에 무엇을 하나요",
  guideAgeBody:
    "종이 초점책은 0~3세, 보호자가 넘겨 보여 주세요. 전자책 함께 보기는 2세부터 — 24개월 미만은 화면을 권하지 않아요(미국소아과학회 권고). 체험 공간과 카메라 마법은 4~7세가 직접 누르고 걷습니다. 화면 보는 시간은 2~5세 하루 1시간 안쪽으로, 늘 곁에서 함께 봐 주세요.",
  guideCameraTitle: "📷 카메라를 쓸 때",
  guideCameraBody:
    "밝은 곳에서, 보호자가 함께 비춰 주세요. 카메라 화면과 찍은 사진은 이 기기 안에서만 만들어지고 서버로 보내지 않아요 — 저장이나 공유는 직접 누를 때만 됩니다. 카메라가 안 되는 곳(카카오톡 안 브라우저·교실 태블릿)에서는 영상으로 같은 장면을 볼 수 있어요.",
  askTitle: "🙋 하고 싶은 말이 있나요?",
  askBody:
    "이곳은 읽는 곳이에요. 아이들이 안전하게 볼 수 있도록 댓글·글쓰기는 두지 않았어요. 문의나 제보는 보호자님이 아래로 보내주세요.",
  askCta: "문의·제보 보내기(외부 폼) ↗",
  endTitle: "🎉 다 읽었어요!",
  endSub: "그레그와 병아리랑 끝까지 걸었어요.",
  endAgain: "🔄 처음부터 다시",
  endMore: "📚 다른 책 보기",
  endWorld: "🏘 체험 공간에서 만나기",
  endTogether: "👨‍👩‍👧 오늘 함께 본 마법",
  artStyle: "",
  storyWho: "내가|그레그가|병아리가|검은 고양이가|친구들이|우리 모두",
  storyWhere: "첨성대에서|도서관에서|마을 광장에서|다리 위에서|무대 위에서|교실에서|별빛 아래서",
  storyWhat: "별을 셌어요|책을 읽었어요|폴짝 뛰었어요|친구를 만났어요|노래를 불렀어요|길을 잃었어요|빵을 나눠 먹었어요|잠이 들었어요",
  storyTitles: "{이름}의 모험|별을 찾아서|경주 한 바퀴|폴짝폴짝 하루|도서관의 비밀",
  storyStickers:
    "npc.greg_white|npc.chick|npc.greg_cat|decor.lantern|decor.tree|decor.lotus|decor.stonelamp|decor.rock|decor.signpost|decor.observatory|decor.hanok|decor.bookshelf",
  theme: DEFAULT_PALETTE_ID,
};

/** 항목별 길이 상한 — 레이아웃이 깨지지 않는 선. */
export const SITE_TEXT_MAX: Record<keyof SiteText, number> = {
  milestone: 20,
  title1: 30,
  title2: 30,
  ageBands: 160,
  bubble: 30,
  cta: 20,
  note: 80,
  makeHeroCta: 20,
  newsEmpty: 60,
  bookKicker: 12,
  bookTitle: 28,
  bookSub: 40,
  resumeCta: 16,
  verseTitle: 24,
  verseEnter: 20,
  verseNoteNew: 90,
  verseNoteSaved: 60,
  guideTitle: 24,
  guideIntro: 220,
  guideSafetyTitle: 24,
  guideSafetyBody: 400,
  guidePrivacyTitle: 24,
  guidePrivacyBody: 400,
  guideClassTitle: 30,
  guideClassBody: 400,
  familyBookTitle: 24,
  familyBookNoteTitle: 24,
  familyBookNoteBody: 300,
  familyBookLinkLabel: 20,
  familyBookLinkUrl: 300,
  guideTogetherTitle: 24,
  guideTogetherBody: 400,
  guideAgeTitle: 24,
  guideAgeBody: 400,
  guideCameraTitle: 24,
  guideCameraBody: 400,
  askTitle: 28,
  askBody: 160,
  askCta: 28,
  endTitle: 24,
  endSub: 40,
  endAgain: 16,
  endMore: 16,
  endWorld: 20,
  endTogether: 24,
  artStyle: 500,
  storyWho: 200,
  storyWhere: 200,
  storyWhat: 240,
  storyTitles: 200,
  storyStickers: 400,
  theme: 16,
};

/**
 * 빈 문자열을 **그대로 두는** 항목 — 비우는 것이 "그 칸을 안 보이게 하기"인 자리들.
 * 나머지는 비면 기본값으로 되돌린다(실수로 화면이 텅 비지 않게).
 *
 * - `milestone` : 제목 위 작은 배지 숨기기
 * - 보호자 안내의 **본문 넷** : 안 쓸 칸은 내용을 비워 그 칸만 없앤다.
 *   제목은 뺀다 — 제목만 남고 본문이 사라지면 이상하고, 본문이 있는데 제목이 없으면 더 이상하다.
 */
const OPTIONAL: readonly (keyof SiteText)[] = [
  "milestone",
  // 연령 딱지 — 기본이 빈 값이다(운영자가 넣는다). 비우면 딱지가 사라져야 한다
  "ageBands",
  /**
   * 🔴 **여기 없으면 「비우면 사라진다」가 거짓말이 된다.**
   *
   * 이 칸을 안 넣고 만들 뻔했다. 그러면 운영자가 지워도 기본값이 되살아나서
   * 첫 화면의 단추가 **안 없어진다** — 주석은 없앨 수 있다고 적혀 있는데.
   */
  "makeHeroCta",
  // 다 읽은 뒤 화면 — 아랫줄과 체험공간 버튼은 **비워서 없앨 수 있게** 둔다.
  // (축하 한 줄·「다시」·「다른 책」은 비면 화면이 막다른 길이 되므로 뺀다)
  "endSub",
  "endWorld",
  "endTogether",
  // 그림체 — 비우면 코드의 기본 결을 쓴다(빈 값이 곧 「기본으로」다)
  "artStyle",
  "guideIntro",
  "guideSafetyBody",
  "guidePrivacyBody",
  /**
   * 🔴 **이 칸이 빠져 있었다** — 그리고 넷 중 제일 위험한 칸이었다.
   *
   * 방과후 수업을 안 하는 곳에서 이 칸을 비우면 기본 글이 되살아난다:
   * 「수업에서 만든 그림책은 반 친구들과 선생님이 볼 수 있게 **저희가 보관해요**」.
   * 하지도 않는 일을, 그것도 **무엇을 저장하는가**에 대해 보호자에게 말하게 된다.
   * 지우는 길을 막아 놓고 「비우면 그 칸만 사라진다」라고 적어 둔 셈이었다.
   */
  "guideClassBody",
  "guideTogetherBody",
];

const KEYS = Object.keys(DEFAULT_SITE_TEXT) as (keyof SiteText)[];

/** 외부 입력 정화(불변식 7). 모르는 키는 버리고, 태그 문자는 제거하고, 길이를 자른다. */
export function sanitizeSiteText(raw: unknown): SiteText {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const out = { ...DEFAULT_SITE_TEXT };
  for (const k of KEYS) {
    const v = o[k];
    if (typeof v !== "string") continue;
    const clean = v
      .replace(/[<>]/g, "") // 태그 주입 차단(다른 렌더 경로가 생겨도 안전하게)
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, SITE_TEXT_MAX[k]);
    if (clean) out[k] = clean;
    else if (OPTIONAL.includes(k)) out[k] = "";
  }
  // 팔레트는 자유 문자열이 아니다 — 목록에 없으면 기본값(대비가 검증된 것만 쓴다)
  out.theme = paletteById(out.theme).id;
  return out;
}

